import type React from "react";
import { Component, type ChangeEvent, type KeyboardEvent } from "react";

interface AutogrowInputProps {
    className?: string;
    defaultValue?: any;
    extraWidth?: number | string;
    id?: string;
    injectStyles?: boolean;
    inputClassName?: string;
    inputRef?: (el: HTMLInputElement | null) => void;
    inputStyle?: React.CSSProperties;
    minWidth?: number | string;
    onAutosize?: (newWidth: number) => void;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
    onBlur?: () => void;
    placeholder?: string;
    placeholderIsMinWidth?: boolean;
    style?: React.CSSProperties;
    value?: any;
    type?: string;
}

interface AutogrowInputState {
    inputWidth: number;
    inputId: string | undefined;
    prevId: string | undefined;
}

const sizerStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    visibility: "hidden",
    height: 0,
    overflow: "scroll",
    whiteSpace: "pre",
} as const;

const INPUT_PROPS_BLACKLIST = [
    "extraWidth",
    "injectStyles",
    "inputClassName",
    "inputRef",
    "inputStyle",
    "minWidth",
    "onAutosize",
    "placeholderIsMinWidth",
] as const;

const cleanInputProps = (inputProps: Record<string, any>): Record<string, any> => {
    INPUT_PROPS_BLACKLIST.forEach((field) => delete inputProps[field]);
    return inputProps;
};

const copyStyles = (styles: CSSStyleDeclaration, node: HTMLElement): void => {
    node.style.fontSize = styles.fontSize;
    node.style.fontFamily = styles.fontFamily;
    node.style.fontWeight = styles.fontWeight;
    node.style.fontStyle = styles.fontStyle;
    node.style.letterSpacing = styles.letterSpacing;
    node.style.textTransform = styles.textTransform;
};

const isIE =
    typeof window !== "undefined" && window.navigator
        ? /MSIE |Trident\/|Edge\//.test(window.navigator.userAgent)
        : false;

const generateId = () => {
    return isIE ? "_" + Math.random().toString(36).substr(2, 12) : undefined;
};

class AutosizeInput extends Component<AutogrowInputProps, AutogrowInputState> {
    private mounted: boolean = false;
    private input: HTMLInputElement | null = null;
    private placeHolderSizer: HTMLDivElement | null = null;
    private sizer: HTMLDivElement | null = null;

    static defaultProps = {
        minWidth: 1,
        injectStyles: true,
    };

    static getDerivedStateFromProps(props: AutogrowInputProps, state: AutogrowInputState) {
        const { id } = props;
        return id !== state.prevId ? { inputId: id || generateId(), prevId: id } : null;
    }

    constructor(props: AutogrowInputProps) {
        super(props);
        this.state = {
            inputWidth: typeof props.minWidth === "number" ? props.minWidth : 1,
            inputId: props.id || generateId(),
            prevId: props.id,
        };
    }

    componentDidMount() {
        this.mounted = true;
        this.copyInputStyles();
        this.updateInputWidth();
    }

    componentDidUpdate(prevProps: AutogrowInputProps, prevState: AutogrowInputState) {
        if (prevState.inputWidth !== this.state.inputWidth) {
            if (typeof this.props.onAutosize === "function") {
                this.props.onAutosize(this.state.inputWidth);
            }
        }
        this.updateInputWidth();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    inputRef = (el: HTMLInputElement | null) => {
        this.input = el;
        if (typeof this.props.inputRef === "function") {
            this.props.inputRef(el);
        }
    };

    placeHolderSizerRef = (el: HTMLDivElement | null) => {
        this.placeHolderSizer = el;
    };

    sizerRef = (el: HTMLDivElement | null) => {
        this.sizer = el;
    };

    copyInputStyles() {
        if (!this.mounted || !window.getComputedStyle || !this.input) {
            return;
        }
        const inputStyles = window.getComputedStyle(this.input);
        if (this.sizer) {
            copyStyles(inputStyles, this.sizer);
        }
        if (this.placeHolderSizer) {
            copyStyles(inputStyles, this.placeHolderSizer);
        }
    }

    updateInputWidth() {
        if (!this.mounted || !this.sizer || typeof this.sizer.scrollWidth === "undefined") {
            return;
        }

        let newInputWidth: number;
        if (
            this.props.placeholder &&
            (!this.props.value || (this.props.value && this.props.placeholderIsMinWidth))
        ) {
            newInputWidth =
                Math.max(this.sizer.scrollWidth, this.placeHolderSizer?.scrollWidth || 0) + 2;
        } else {
            newInputWidth = this.sizer.scrollWidth + 2;
        }

        // add extraWidth to the detected width. for number types, this defaults to 16 to allow for the stepper UI
        const extraWidth =
            this.props.type === "number" && this.props.extraWidth === undefined
                ? 16
                : parseInt(String(this.props.extraWidth)) || 0;
        newInputWidth += extraWidth;

        if (this.props.minWidth && newInputWidth < this.props.minWidth) {
            newInputWidth =
                typeof this.props.minWidth === "number"
                    ? this.props.minWidth
                    : parseInt(this.props.minWidth);
        }

        if (newInputWidth !== this.state.inputWidth) {
            this.setState({
                inputWidth: newInputWidth,
            });
        }
    }

    getInput() {
        return this.input;
    }

    focus() {
        this.input?.focus();
    }

    blur() {
        this.input?.blur();
    }

    select() {
        this.input?.select();
    }

    renderStyles() {
        const { injectStyles } = this.props;
        return isIE && injectStyles ? (
            <style
                dangerouslySetInnerHTML={{
                    __html: `input#${this.state.inputId}::-ms-clear {display: none;}`,
                }}
            />
        ) : null;
    }

    render() {
        const sizerValue = [this.props.defaultValue, this.props.value, ""].reduce(
            (previousValue, currentValue) => {
                if (previousValue !== null && previousValue !== undefined) {
                    return previousValue;
                }
                return currentValue;
            }
        );

        const wrapperStyle = { ...this.props.style };
        if (!wrapperStyle.display) wrapperStyle.display = "inline-block";

        const inputStyle = {
            boxSizing: "content-box",
            width: `${this.state.inputWidth}px`,
            ...this.props.inputStyle,
        };

        const { ...inputProps } = this.props;
        cleanInputProps(inputProps);
        inputProps.className = this.props.inputClassName;
        inputProps.id = this.state.inputId;
        inputProps.style = inputStyle;

        return (
            <div className={this.props.className} style={wrapperStyle}>
                {this.renderStyles()}
                <input {...inputProps} ref={this.inputRef} />
                <div ref={this.sizerRef} style={sizerStyle}>
                    {sizerValue}
                </div>
                {this.props.placeholder ? (
                    <div ref={this.placeHolderSizerRef} style={sizerStyle}>
                        {this.props.placeholder}
                    </div>
                ) : null}
            </div>
        );
    }
}

export default AutosizeInput;
